import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SimpleHeader from '../components/SimpleHeader';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Lock, Zap, Gift, Check } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { createCheckoutSession, getCustomerPurchases } from '../services/paymentService';

const MainGuidePage = () => {
  const [email, setEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const bonuses = [
    { name: 'Printable Audit Checklists', value: '$19' },
    { name: 'Weekly Spending Planner', value: '$12' },
    { name: 'Plant Starter List (Climate-Specific)', value: '$15' },
    { name: 'DIY Project Cheat Sheets', value: '$24' },
    { name: '90-Day Implementation Plan', value: '$29' }
  ];

  const handlePurchase = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const emailToUse = email || null;
      const { checkout_url } = await createCheckoutSession('main_guide_launch', emailToUse);
      window.location.href = checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast({ title: 'Error', description: 'Failed to create checkout session. Please try again.', variant: 'destructive' });
      setIsProcessing(false);
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast({ title: 'Invalid Email', description: 'Please enter a valid email address.', variant: 'destructive' });
      return;
    }
    try {
      const { purchases } = await getCustomerPurchases(email);
      if (purchases.length > 0) {
        toast({ title: 'Access Granted!', description: `Found ${purchases.length} purchase(s). Redirecting...`, variant: 'default' });
        setTimeout(() => { window.location.href = '/access'; }, 1500);
      } else {
        toast({ title: 'No Purchases Found', description: "We couldn't find any purchases with this email.", variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to verify purchases.', variant: 'destructive' });
    }
  };

  return (
    <>
      <div className="min-h-screen bg-[#F5F1E8]">
        <SimpleHeader />
        
        <main className="pt-24 pb-20 px-6">
          <div className="max-w-3xl mx-auto">
            {/* Branded Banner */}
            <div className="rounded-xl overflow-hidden shadow-xl mb-10">
              <img 
                src="/images/spendless-banner.png" 
                alt="Spend Less. Live More." 
                className="w-full h-auto"
                data-testid="product-banner-img"
              />
            </div>
            
            <div className="text-center">
              {/* Launch Special Banner */}
              <div className="bg-gradient-to-r from-[#C17855] to-[#B06745] text-white p-4 rounded-lg mb-6 shadow-lg">
                <div className="flex items-center justify-center gap-3">
                  <Zap className="w-6 h-6 animate-pulse" />
                  <p className="font-bold">Launch Special: Get 33% Off + $99 in FREE Bonuses!</p>
                  <Zap className="w-6 h-6 animate-pulse" />
                </div>
              </div>
              
              <div className="flex items-center justify-center gap-3 mb-4">
                <img src="/images/spendless-logo.png" alt="SpendLess" className="w-14 h-14 rounded-full shadow-md" data-testid="product-logo-img" />
                <p className="text-[#6B7A5C] text-sm uppercase tracking-wider font-semibold">Premium Content</p>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-[#2C1810] mb-4" data-testid="main-guide-title">
                SpendLess Digital Guide
              </h1>
              
              <p className="text-[#5A5046] text-xl mb-12">
                The complete system for cutting your expenses 20-50% — 7 chapters, 30/60/90-day action plan
              </p>
              
              <Card className="bg-white border-2 border-[#E5DFD0] shadow-xl p-12 max-w-xl mx-auto">
                {/* Pricing Display */}
                <div className="mb-6">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <span className="text-2xl text-[#5A5046] line-through">$14.99</span>
                    <div className="bg-[#C17855] text-white px-3 py-1 rounded-full text-xs font-bold">SAVE 33%</div>
                  </div>
                  <div className="text-5xl font-bold text-[#C17855] mb-2">$9.99</div>
                  <p className="text-[#5A5046] text-sm">Launch price - Regular: $14.99</p>
                </div>

                {/* Bonuses Section */}
                <div className="bg-[#6B7A5C] bg-opacity-10 border-2 border-[#6B7A5C] border-dashed rounded-lg p-6 mb-6">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Gift className="w-5 h-5 text-[#6B7A5C]" />
                    <h3 className="text-lg font-bold text-[#2C1810]">+ FREE Bonuses Worth $99</h3>
                  </div>
                  <div className="space-y-2 text-sm text-left">
                    {bonuses.map((bonus, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-[#6B7A5C]" />
                          <span className="text-[#2C1810]">{bonus.name}</span>
                        </div>
                        <span className="text-[#6B7A5C] font-bold">{bonus.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-[#6B7A5C] border-dashed">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-[#2C1810] text-sm">Total Bonus Value:</span>
                      <span className="font-bold text-[#C17855]">$99</span>
                    </div>
                  </div>
                </div>
                
                <Button 
                  onClick={handlePurchase}
                  disabled={isProcessing}
                  className="w-full bg-[#C17855] hover:bg-[#B06745] text-white px-8 py-6 text-lg rounded-lg transition-all shadow-md hover:shadow-lg mb-4"
                  data-testid="main-guide-purchase-btn"
                >
                  {isProcessing ? 'Redirecting to checkout...' : 'Get Guide + All Bonuses — $9.99'}
                </Button>
                
                <p className="text-[#5A5046] text-xs mb-8">
                  Price increases to $14.99 after first 100 sales
                </p>
                
                <div className="border-t border-[#E5DFD0] pt-8">
                  <p className="text-[#2C1810] font-semibold mb-4">Already purchased?</p>
                  <form onSubmit={handleUnlock} className="space-y-4">
                    <p className="text-sm text-[#5A5046] mb-4">Enter your email to unlock your content:</p>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="w-full px-6 py-3 rounded-lg border-[#E5DFD0]" data-testid="main-guide-email-input" />
                    <Button type="submit" variant="outline" className="w-full border-2 border-[#6B7A5C] text-[#6B7A5C] hover:bg-[#6B7A5C] hover:text-white px-6 py-3 rounded-lg transition-all" data-testid="main-guide-unlock-btn">Unlock</Button>
                  </form>
                </div>
              </Card>
              
              <div className="mt-12">
                <Link to="/free-guide" className="text-[#6B7A5C] hover:underline">Back to Free Guide</Link>
              </div>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
      <Toaster />
    </>
  );
};

export default MainGuidePage;
